import { useEffect, useRef, useState } from 'react'
import {
  ArrowRight,
  Copy,
  Eye,
  EyeOff,
  Phone,
  PhoneCall,
  Send,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { JarvisOrb, type OrbState } from '@/components/JarvisOrb'
import { JarvisCard } from '@/components/JarvisCard'
import { UsageChip } from '@/components/UsageSettings'
import { StatCard } from '@/components/StatCard'
import { ActivityItem } from '@/components/ActivityItem'
import { BriefingCard } from '@/components/BriefingCard'
import { TimeSavedWidget } from '@/components/TimeSavedWidget'
import { VibeSelectorCompact } from '@/components/VibeSelectorCompact'
import { useToast } from '@/components/Toast'
import { usePersona } from '@/lib/personas'
import {
  useActivity,
  useAdsReport,
  useCurrentUser,
  useMetrics,
  useTasks,
} from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import { useWebCall, type CallStatus } from '@/lib/useWebCall'
import { useClapDetection } from '@/lib/useClapDetection'
import { effectiveVibe, type Vibe, type VibePersona } from '@/lib/vibes'
import { formatCents, formatPhone, formatRoas } from '@/lib/format'

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning.'
  if (hour < 17) return 'Good afternoon.'
  return 'Good evening.'
}

const dateLabel = new Date().toLocaleDateString('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
})

const HIDE_PHONE_KEY = 'jarvis.hidePhoneInUi'

function maskPhone(formatted: string): string {
  return formatted.replace(/\d/g, '•')
}

// Map the call lifecycle to the orb's existing visual states. We reuse
// idle / listening / thinking / speaking rather than adding new pulse
// animations — the semantics already line up:
//   connecting  → thinking (faster than idle, signals "working")
//   connected   → listening (steady bright pulse, user's turn)
//   speaking    → speaking (fast bright pulse, agent talking)
function callStatusToOrbState(status: CallStatus): OrbState {
  switch (status) {
    case 'connecting':
      return 'thinking'
    case 'connected':
      return 'listening'
    case 'speaking':
      return 'speaking'
    case 'idle':
    case 'ended':
    case 'error':
    default:
      return 'idle'
  }
}

function orbLabel(status: CallStatus, wordmark: string): string | null {
  switch (status) {
    case 'connecting':
      return `Waking ${wordmark}…`
    case 'connected':
      return `${wordmark} is listening`
    case 'speaking':
      return `${wordmark} is speaking`
    case 'ended':
      return 'Call ended'
    case 'error':
      return 'Tap to retry'
    case 'idle':
    default:
      return null
  }
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m}m` : `${m}m ${s}s`
}

export function CommandCenter() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { persona, personaId } = usePersona()
  const me = useCurrentUser()

  // ── Chat (text) state ────────────────────────────────────────────────────
  // Kept independent from the call orb. Chat thinking lights the orb only when
  // there's no active call (callStatus takes priority).
  const [chatOrbThinking, setChatOrbThinking] = useState(false)
  const [message, setMessage] = useState('')
  const [response, setResponse] = useState<string | null>(null)

  // ── Phone-number visibility (screen-share-safe mask) ─────────────────────
  const [copied, setCopied] = useState(false)
  const [phoneHidden, setPhoneHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HIDE_PHONE_KEY) === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(HIDE_PHONE_KEY, phoneHidden ? '1' : '0')
    } catch {
      /* private mode — ignore */
    }
  }, [phoneHidden])

  // ── In-app voice call ────────────────────────────────────────────────────
  // Vibe + custom greeting come straight from the user doc. effectiveVibe
  // clamps to whatever the active persona allows so we never send a locked
  // vibe to the call.
  const userVibe = (me.data?.settings as { callVibe?: Vibe } | undefined)?.callVibe ?? 'professional'
  const customGreeting = (me.data?.settings as { callGreeting?: string } | undefined)?.callGreeting ?? null
  const clapEnabled = (me.data?.settings as { clapToCallEnabled?: boolean } | undefined)?.clapToCallEnabled ?? false
  const clapSensitivity = (me.data?.settings as { clapSensitivity?: number } | undefined)?.clapSensitivity ?? 0.35

  const resolvedVibe = effectiveVibe(personaId as VibePersona, userVibe)

  const webCall = useWebCall({
    persona: personaId as VibePersona,
    vibe: resolvedVibe,
    customGreeting,
  })

  // Track call duration so we can include it in the end-of-call toast
  const callStartRef = useRef<number | null>(null)
  useEffect(() => {
    if (webCall.status === 'connected' && callStartRef.current === null) {
      callStartRef.current = Date.now()
    }
    if (webCall.status === 'ended' && callStartRef.current !== null) {
      const seconds = Math.round((Date.now() - callStartRef.current) / 1000)
      callStartRef.current = null
      toast.success('Call ended', `${formatDuration(seconds)} · Transcript saving…`)
    }
  }, [webCall.status, toast])

  useEffect(() => {
    if (webCall.error) toast.error('Voice call problem', webCall.error)
  }, [webCall.error, toast])

  // Wake-word event → start the call. WakeWord component listens for the
  // phrase and dispatches `jarvis-wake`; here we turn that into action.
  useEffect(() => {
    const onWake = () => {
      if (webCall.status === 'idle' || webCall.status === 'ended') {
        void webCall.start()
      }
    }
    window.addEventListener('jarvis-wake', onWake)
    return () => window.removeEventListener('jarvis-wake', onWake)
  }, [webCall])

  // Clap-to-call (opt-in) — same effect as wake event
  useClapDetection(
    () => {
      if (webCall.status === 'idle' || webCall.status === 'ended') {
        void webCall.start()
      }
    },
    { enabled: clapEnabled, sensitivity: clapSensitivity },
  )

  // Update the user's stored vibe — persists via PATCH /api/settings
  const saveVibe = useMutation({
    mutationFn: (vibe: Vibe) =>
      unwrap(apiClient.patch('/settings', { callVibe: vibe })),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] }),
  })

  // ── Other dashboard data ─────────────────────────────────────────────────
  const ads = useAdsReport('last_7d')
  const metrics = useMetrics()
  const activity = useActivity()
  const tasks = useTasks('pending')

  const phoneNumber = me.data?.acsPhoneNumber ?? null
  const voiceEnabled = me.data?.voiceEnabled ?? false
  const tier = me.data?.subscriptionTier
  const tierLabel = tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : null

  const handleCopyPhone = async () => {
    if (!phoneNumber) return
    try {
      await navigator.clipboard.writeText(phoneNumber)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard API unavailable — ignore */
    }
  }

  const callMe = useMutation({
    mutationFn: () =>
      unwrap<{ callId: string; toNumber: string; fromNumber: string }>(
        apiClient.post('/voice/call-me'),
      ),
    onSuccess: (data) => {
      toast.success(
        `${persona.wordmark} is calling you`,
        `Dialing ${formatPhone(data.toNumber)} from ${formatPhone(data.fromNumber)}`,
      )
    },
    onError: (e) => toast.error('Could not start the call', (e as Error).message),
  })
  const canCallMe = Boolean(
    me.data?.mobilePhone && me.data?.acsPhoneNumber && me.data?.voiceEnabled,
  )

  const chat = useMutation({
    mutationFn: async (text: string) => {
      setChatOrbThinking(true)
      try {
        const res = await unwrap<{ reply: string }>(
          apiClient.post('/jarvis/chat', { message: text }),
        )
        return res.reply
      } finally {
        setChatOrbThinking(false)
      }
    },
    onSuccess: (reply) => setResponse(reply),
    onError: (e) => setResponse((e as Error).message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return
    chat.mutate(message)
    setMessage('')
  }

  // Orb visual state — call status wins over chat-thinking, idle when neither
  const orbState: OrbState =
    webCall.status === 'idle'
      ? chatOrbThinking
        ? 'thinking'
        : 'idle'
      : callStatusToOrbState(webCall.status)

  const orbStatusText = orbLabel(webCall.status, persona.wordmark)
  const orbCallActionLabel =
    webCall.isActive ? 'End call' : `Tap to talk to ${persona.wordmark}`

  const briefingLines = [
    ads.data?.byScore.pause
      ? `${ads.data.byScore.pause} ads recommended to pause`
      : null,
    ads.data?.byScore.scale
      ? `${ads.data.byScore.scale} ads ready to scale`
      : null,
    metrics.data?.live
      ? `MRR ${formatCents(metrics.data.live.mrrCents)} (${metrics.data.live.activeSubscriptions} active)`
      : null,
    metrics.data?.live.trialingSubscriptions
      ? `${metrics.data.live.trialingSubscriptions} trials in flight`
      : null,
    tasks.data?.items.length
      ? `${tasks.data.items.length} open tasks${tasks.data.items.filter((t) => t.priority === 'urgent').length ? ` (${tasks.data.items.filter((t) => t.priority === 'urgent').length} urgent)` : ''}`
      : null,
  ].filter(Boolean) as string[]

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{getGreeting()}</h1>
          <p className="text-sm text-jarvis-muted mt-1">{dateLabel}</p>
        </div>
        <div className="pt-1">
          <UsageChip />
        </div>
      </div>

      {(phoneNumber || me.data?.subscriptionStatus === 'none') && (
        <JarvisCard>
          {phoneNumber ? (
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-jarvis-muted">
                  Your Jarvis Line
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <Phone size={18} className="text-jarvis-blue" />
                  <span
                    className="text-2xl font-semibold tracking-tight"
                    aria-label={phoneHidden ? 'Phone hidden' : formatPhone(phoneNumber)}
                  >
                    {phoneHidden
                      ? maskPhone(formatPhone(phoneNumber))
                      : formatPhone(phoneNumber)}
                  </span>
                </div>
                <p className="text-xs text-jarvis-muted mt-1.5">
                  {voiceEnabled
                    ? phoneHidden
                      ? 'Number hidden in UI — outbound caller ID is unchanged. Click the eye icon to reveal.'
                      : `${persona.wordmark} is live on this number. Share it and let your AI answer.`
                    : 'Number provisioned but voice not yet enabled — finish ElevenLabs setup.'}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setPhoneHidden((v) => !v)}
                  className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-jarvis-border hover:border-jarvis-blue/60 hover:bg-jarvis-surface-2 transition"
                  aria-label={phoneHidden ? 'Show phone number' : 'Hide phone number'}
                  title={phoneHidden ? 'Show number' : 'Hide for screen-share'}
                >
                  {phoneHidden ? <Eye size={12} /> : <EyeOff size={12} />}
                  {phoneHidden ? 'Show' : 'Hide'}
                </button>
                <button
                  onClick={handleCopyPhone}
                  disabled={phoneHidden}
                  className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-jarvis-border hover:border-jarvis-blue/60 hover:bg-jarvis-surface-2 transition disabled:opacity-40"
                  aria-label="Copy phone number"
                >
                  <Copy size={12} />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-jarvis-muted">
                  Voice agent
                </div>
                <p className="text-sm mt-2">
                  {tierLabel
                    ? `Your ${tierLabel} plan includes a dedicated phone line — Jarvis is provisioning a number now. Refresh in a moment.`
                    : 'No phone number yet. Subscribe to a plan and Jarvis will provision a number automatically.'}
                </p>
              </div>
              <a
                href="/billing"
                className="text-xs px-3 py-1.5 rounded-md bg-jarvis-blue hover:bg-jarvis-blue-bright transition text-jarvis-surface"
              >
                Upgrade
              </a>
            </div>
          )}
        </JarvisCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* The orb IS the call button. Click → start a live in-app voice call
            with the active persona. Click again while in-call → hang up. */}
        <JarvisCard className="flex flex-col items-center justify-center min-h-[320px] gap-3">
          <button
            type="button"
            onClick={() => {
              if (webCall.isActive) {
                void webCall.end()
              } else {
                void webCall.start()
              }
            }}
            aria-label={orbCallActionLabel}
            title={orbCallActionLabel}
            className="group relative bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-jarvis-blue/60 rounded-full"
          >
            <JarvisOrb state={orbState} size="lg" />
          </button>
          {orbStatusText ? (
            <div className="text-xs uppercase tracking-widest text-jarvis-blue">
              {orbStatusText}
            </div>
          ) : (
            <div className="text-xs text-jarvis-muted opacity-0 group-hover:opacity-100 transition-opacity">
              Tap to talk to {persona.wordmark}
            </div>
          )}
          {webCall.isActive && (
            <button
              type="button"
              onClick={() => void webCall.end()}
              className="text-xs px-3 py-1.5 rounded-md border border-jarvis-red/60 text-jarvis-red hover:bg-jarvis-red/10 transition"
            >
              End call
            </button>
          )}

          {/* Vibe selector — quick-switch row beneath the orb */}
          <div className="mt-2">
            <VibeSelectorCompact
              currentVibe={resolvedVibe}
              personaId={personaId as VibePersona}
              onChange={(v) => saveVibe.mutate(v)}
              dense
            />
            <p className="text-xs text-jarvis-muted text-center mt-1.5">
              <Link to="/settings#voice" className="hover:text-jarvis-text">
                Change your vibe →
              </Link>
            </p>
          </div>
        </JarvisCard>

        <JarvisCard>
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
            Today's Signals
          </h2>
          <ul className="mt-4 space-y-2 text-sm">
            {briefingLines.length === 0 ? (
              <li className="text-jarvis-muted">No signals yet — sync ads and connect Stripe.</li>
            ) : (
              briefingLines.map((line) => (
                <li key={line} className="flex gap-2">
                  <span className="text-jarvis-blue mt-1">•</span>
                  <span>{line}</span>
                </li>
              ))
            )}
          </ul>
          {tasks.data?.items[0] && (
            <div className="mt-5 pt-4 border-t border-jarvis-border">
              <div className="text-xs uppercase tracking-widest text-jarvis-muted">
                Recommended
              </div>
              <p className="text-sm mt-1.5">{tasks.data.items[0].title}</p>
            </div>
          )}
        </JarvisCard>
      </div>

      <BriefingCard />

      {/* PSTN "call me on my mobile" lives behind a disclosure so the in-app
          call (the orb) is the obvious primary affordance. */}
      <JarvisCard>
        <details className="text-sm">
          <summary className="cursor-pointer text-jarvis-muted hover:text-jarvis-text select-none">
            More voice options
          </summary>
          <div className="mt-3 flex items-start justify-between gap-3 flex-wrap">
            <div className="text-xs text-jarvis-muted max-w-md">
              Have {persona.wordmark} ring your mobile instead. Useful when you're
              away from the dashboard. Set your mobile in Settings → Daily Briefing.
            </div>
            <button
              onClick={() => callMe.mutate()}
              disabled={!canCallMe || callMe.isPending}
              title={
                canCallMe
                  ? `Have ${persona.wordmark} call your mobile right now`
                  : 'Add a mobile number in Briefing Settings first'
              }
              className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-jarvis-blue text-white hover:bg-jarvis-blue-bright transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <PhoneCall size={12} />
              {callMe.isPending ? 'Dialing…' : `Have ${persona.wordmark} call me`}
            </button>
          </div>
        </details>
      </JarvisCard>

      <JarvisCard>
        <form onSubmit={handleSubmit} className="flex gap-3">
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Ask ${persona.wordmark} anything…`}
            className="flex-1 bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-jarvis-blue/60"
            disabled={chat.isPending}
          />
          <button
            type="submit"
            disabled={chat.isPending || !message.trim()}
            className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-4 rounded-lg flex items-center justify-center transition-colors"
          >
            {chat.isPending ? <ArrowRight size={18} /> : <Send size={18} />}
          </button>
        </form>
        {response && (
          <div className="mt-4 p-4 rounded-lg bg-jarvis-surface-2 border border-jarvis-border text-sm whitespace-pre-wrap">
            {response}
          </div>
        )}
      </JarvisCard>

      <JarvisCard>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
            Live Activity
          </h2>
          <a href="/activity" className="text-xs text-jarvis-blue hover:underline">
            View all →
          </a>
        </div>
        <div>
          {activity.isLoading && <div className="text-jarvis-muted text-sm">Loading…</div>}
          {activity.data?.items.slice(0, 5).map((item) => (
            <ActivityItem key={`${item.type}-${item.timestamp}`} item={item} />
          ))}
          {activity.data?.items.length === 0 && (
            <div className="text-jarvis-muted text-sm">No activity yet.</div>
          )}
        </div>
      </JarvisCard>

      <TimeSavedWidget />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <StatCard
          label="MRR"
          value={metrics.data?.live ? formatCents(metrics.data.live.mrrCents) : '—'}
          delta={
            metrics.data?.live.netNewMrrCents
              ? {
                  value: formatCents(Math.abs(metrics.data.live.netNewMrrCents)),
                  direction: metrics.data.live.netNewMrrCents >= 0 ? 'up' : 'down',
                  sentiment: metrics.data.live.netNewMrrCents >= 0 ? 'good' : 'bad',
                }
              : undefined
          }
        />
        <StatCard
          label="Active Trials"
          value={metrics.data?.live.trialingSubscriptions ?? '—'}
        />
        <StatCard
          label="Open Tasks"
          value={tasks.data?.items.length ?? 0}
          hint={
            tasks.data?.items.filter((t) => t.priority === 'urgent').length
              ? `${tasks.data.items.filter((t) => t.priority === 'urgent').length} urgent`
              : undefined
          }
        />
      </div>

      <div className="text-xs text-jarvis-muted">
        Blended ROAS:{' '}
        {ads.data?.totals.blendedRoas
          ? formatRoas(ads.data.totals.blendedRoas)
          : '—'}
      </div>
    </div>
  )
}
